import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, DollarSign, Calendar, TrendingUp,
  Loader2, Truck, AlertTriangle, RefreshCw
} from "lucide-react";

import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// ── THEME (mirrors PostLoadPage's token system so both pages feel like
// one product) — CSS variables toggled via data-an-theme on the root. ──
const T = {
  bg:"var(--an-bg)", surface:"var(--an-surface)", line:"var(--an-line)", line2:"var(--an-line2)",
  navy:"var(--an-navy)",
  teal:"var(--an-teal)", tealLight:"var(--an-teal-light)",
  amber:"var(--an-amber)", amberLight:"var(--an-amber-light)",
  slate:"var(--an-slate)", muted:"var(--an-muted)",
  text:"var(--an-text)", onAccent:"#ffffff",
  green:"var(--an-green)", greenLight:"var(--an-green-light)",
  blue:"var(--an-blue)", blueLight:"var(--an-blue-light)",
  indigo:"var(--an-indigo)", indigoLight:"var(--an-indigo-light)",
  red:"var(--an-red)", redLight:"var(--an-red-light)",
  shadow:"var(--an-shadow)",
};

const THEME_CSS = `
  [data-an-theme="light"]{
    --an-bg:#f3f4f6; --an-surface:#ffffff; --an-line:#e5e7eb; --an-line2:#f3f4f6;
    --an-navy:#1a2744;
    --an-teal:#0099a8; --an-teal-light:#e6f7f9;
    --an-amber:#e07b00; --an-amber-light:#fef3e2;
    --an-slate:#64748b; --an-muted:#9ca3af;
    --an-text:#111827;
    --an-green:#16a34a; --an-green-light:#f0fdf4;
    --an-blue:#2563eb; --an-blue-light:#eff6ff;
    --an-indigo:#4f46e5; --an-indigo-light:#eef2ff;
    --an-red:#ef4444; --an-red-light:#fef2f2;
    --an-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04);
  }
  [data-an-theme="dark"]{
    --an-bg:#0b0f1a; --an-surface:#121826; --an-line:#232b3d; --an-line2:#161d2c;
    --an-navy:#0d1526;
    --an-teal:#22b8c8; --an-teal-light:#0f2b30;
    --an-amber:#f2a13a; --an-amber-light:#2c2110;
    --an-slate:#94a3b8; --an-muted:#5b6577;
    --an-text:#eef1f6;
    --an-green:#34d399; --an-green-light:#0f2a20;
    --an-blue:#60a5fa; --an-blue-light:#101c30;
    --an-indigo:#818cf8; --an-indigo-light:#191933;
    --an-red:#f87171; --an-red-light:#2a1414;
    --an-shadow: 0 1px 2px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.35);
  }
`;

function ThemeToggle({ theme, setTheme }) {
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={() => setTheme(dark ? "light" : "dark")}
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        display:"inline-flex", alignItems:"center", gap:7, padding:"8px 12px",
        borderRadius:10, border:`1.5px solid ${T.line}`, background:T.surface,
        color:T.slate, fontSize:12, fontWeight:700, cursor:"pointer",
        boxShadow:T.shadow, flexShrink:0,
      }}
    >
      <span style={{ fontSize:14 }}>{dark ? "🌙" : "☀️"}</span>
      <span className="hidden sm:inline">{dark ? "Dark" : "Light"}</span>
    </button>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, accentLight }) {
  return (
    <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
      <div className="absolute top-0 right-0 p-4 opacity-[0.08]">
        <Icon className="w-16 h-16" style={{ color:accent }} />
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background:accentLight, border:`1px solid ${accent}30` }}>
        <Icon className="w-5 h-5" style={{ color:accent }} />
      </div>
      <p className="text-xs font-bold uppercase tracking-wider" style={{ color:T.muted }}>{label}</p>
      <h2 className="text-3xl font-black mt-1" style={{ color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{value}</h2>
      {sub && <p className="text-xs font-medium mt-1.5" style={{ color:T.slate }}>{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNav, setActiveNav] = useState("analytics");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("pl_theme")
        || (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    } catch { return "light"; }
  });
  useEffect(() => { try { localStorage.setItem("pl_theme", theme); } catch {} }, [theme]);

  // Analytics State
  const [timeframe, setTimeframe] = useState("7"); // 7, 14, 30, 180, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState({
    total_spent: 0,
    total_loads: 0,
    chart_data: [],
    start_date: "",
    end_date: ""
  });

  // Auth guard
  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  const fetchAnalytics = async () => {
    if (!user?.email || !token) return;

    setIsLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/analytics/company/?email=${encodeURIComponent(user.email)}&timeframe=${timeframe}`;
      if (timeframe === "custom" && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const result = await res.json();
        if (res.ok) {
          setData({
            total_spent: Number(result.total_spent) || 0,
            total_loads: Number(result.total_loads) || 0,
            chart_data: Array.isArray(result.chart_data) ? result.chart_data : [],
            start_date: result.start_date || "",
            end_date: result.end_date || "",
          });
        } else {
          setError(result?.message || "The server rejected this request.");
        }
      } else {
        setError("The server returned an unexpected response. Please try again.");
      }
    } catch (err) {
      setError("Couldn't reach the analytics service. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch when standard timeframes change
  useEffect(() => {
    if (timeframe !== "custom") {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  const handleCustomApply = () => {
    if (startDate && endDate) fetchAnalytics();
  };

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  // Chart utilities
  const chartData = data.chart_data || [];
  const maxSpend = Math.max(...(chartData.map(d => d.spent) || [0]), 10); // min scale of 10 to prevent flatlining
  const avgPerDay = chartData.length > 0 ? data.total_spent / chartData.length : 0;
  const daysWithActivity = chartData.filter(d => d.loads > 0).length;

  const timeframeLabel = useMemo(() => ({
    "7":"the last 7 days", "14":"the last 14 days", "30":"the last 30 days",
    "180":"the last 6 months", "custom":"your selected range",
  })[timeframe] || "this period", [timeframe]);

  return (
    <div data-an-theme={theme} className="flex h-screen overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif", color:T.text, background:T.bg }}>
      <style dangerouslySetInnerHTML={{__html: `
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');
        ${THEME_CSS}
        [data-an-theme] input::placeholder{color:var(--an-muted);}
        [data-an-theme] input[type="date"]{color-scheme:${theme};}
        [data-an-theme] ::-webkit-scrollbar{width:10px;height:10px;}
        [data-an-theme] ::-webkit-scrollbar-thumb{background:var(--an-line);border-radius:8px;}
      `}} />

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0" style={{ background:T.bg }}>
        <TopBar
          title="Spending Analytics"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

            {/* Header & Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold" style={{ color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Financial Overview</h1>
                  <p className="text-sm mt-1" style={{ color:T.slate }}>Track your load posting expenditures over time.</p>
                </div>
                <div className="lg:hidden">
                  <ThemeToggle theme={theme} setTheme={setTheme} />
                </div>
              </div>

              {/* Timeframe Selector */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="p-1 rounded-xl flex overflow-x-auto w-full sm:w-auto" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
                  {[
                    { val: "7", label: "7 Days" },
                    { val: "14", label: "14 Days" },
                    { val: "30", label: "30 Days" },
                    { val: "180", label: "6 Months" },
                    { val: "custom", label: "Custom" }
                  ].map(t => (
                    <button
                      key={t.val}
                      onClick={() => setTimeframe(t.val)}
                      className="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: timeframe === t.val ? T.navy : "transparent",
                        color: timeframe === t.val ? T.onAccent : T.slate,
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Pickers */}
                {timeframe === "custom" && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="text-xs rounded-lg px-2 py-2 outline-none"
                      style={{ background:T.surface, border:`1px solid ${T.line}`, color:T.text }}
                    />
                    <span className="text-xs" style={{ color:T.muted }}>to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="text-xs rounded-lg px-2 py-2 outline-none"
                      style={{ background:T.surface, border:`1px solid ${T.line}`, color:T.text }}
                    />
                    <button
                      onClick={handleCustomApply}
                      disabled={!startDate || !endDate}
                      className="px-3 py-2 text-xs font-bold rounded-lg disabled:opacity-50"
                      style={{ background:T.teal, color:T.onAccent }}
                    >
                      Apply
                    </button>
                  </div>
                )}

                <div className="hidden lg:block">
                  <ThemeToggle theme={theme} setTheme={setTheme} />
                </div>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-xl p-4" style={{ background:T.redLight, border:`1px solid ${T.red}40` }}>
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color:T.red }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color:T.red }}>Couldn't load analytics</p>
                  <p className="text-xs mt-0.5" style={{ color:T.slate }}>{error}</p>
                </div>
                <button
                  onClick={fetchAnalytics}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                  style={{ background:T.red, color:T.onAccent }}
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color:T.teal }} />
                <p className="text-sm font-medium" style={{ color:T.muted }}>Crunching your numbers…</p>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ color:T.muted }}>
                  Showing data from {data.start_date || "—"} to {data.end_date || "—"} · {timeframeLabel}
                </p>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <KpiCard icon={DollarSign} label="Total Spent" value={`$${data.total_spent.toFixed(2)}`}
                    accent={T.green} accentLight={T.greenLight} />
                  <KpiCard icon={Truck} label="Loads Posted" value={data.total_loads}
                    sub={`${daysWithActivity} active day${daysWithActivity === 1 ? "" : "s"}`}
                    accent={T.blue} accentLight={T.blueLight} />
                  <KpiCard icon={TrendingUp} label="Avg Cost / Load" value={`$${data.total_loads > 0 ? (data.total_spent / data.total_loads).toFixed(2) : "0.00"}`}
                    accent={T.indigo} accentLight={T.indigoLight} />
                  <KpiCard icon={Calendar} label="Avg Spend / Day" value={`$${avgPerDay.toFixed(2)}`}
                    accent={T.amber} accentLight={T.amberLight} />
                </div>

                {/* Chart */}
                <div className="rounded-2xl p-4 sm:p-6" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
                  <div className="flex items-center gap-2.5 mb-4 sm:mb-6 pb-4" style={{ borderBottom:`1px solid ${T.line2}` }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:T.tealLight, border:`1px solid ${T.teal}25` }}>
                      <BarChart3 className="w-4 h-4" style={{ color:T.teal }} />
                    </div>
                    <h3 className="text-base font-bold" style={{ color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Daily Spending Breakdown</h3>
                  </div>

                  {chartData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Calendar className="w-12 h-12 mb-3" style={{ color:T.line }} />
                      <p className="text-sm font-semibold" style={{ color:T.text }}>No spending data</p>
                      <p className="text-xs mt-1" style={{ color:T.muted }}>You didn't post any loads in {timeframeLabel}.</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Gridlines */}
                      <div className="absolute inset-x-0 top-0 h-64 flex flex-col justify-between pointer-events-none pb-6 pt-4">
                        {[0, 1, 2, 3].map(i => (
                          <div key={i} style={{ borderTop:`1px dashed ${T.line}` }} />
                        ))}
                      </div>

                      <div className="h-64 flex items-end gap-2 overflow-x-auto pb-6 pt-4 relative">
                        {chartData.map((day, idx) => {
                          const heightPercent = maxSpend > 0 ? (day.spent / maxSpend) * 100 : 0;

                          return (
                            <div key={idx} className="flex-1 min-w-[36px] max-w-[80px] flex flex-col justify-end group relative h-full">

                              {/* Hover Tooltip */}
                              <div
                                className="opacity-0 group-hover:opacity-100 absolute -top-14 left-1/2 -translate-x-1/2 text-[10px] py-1.5 px-3 rounded-lg pointer-events-none transition-opacity whitespace-nowrap z-10"
                                style={{ background:T.navy, color:T.onAccent, boxShadow:"0 8px 20px rgba(0,0,0,0.25)" }}
                              >
                                <p className="font-bold">${day.spent.toFixed(2)}</p>
                                <p style={{ color:"#9aa5b8" }}>{day.loads} load{day.loads === 1 ? "" : "s"}</p>
                              </div>

                              {/* The Bar */}
                              <div
                                className="w-full rounded-t-md transition-all duration-500 ease-out relative group-hover:brightness-110"
                                style={{
                                  height: `${Math.max(heightPercent, 2)}%`,
                                  background: day.spent > 0
                                    ? `linear-gradient(180deg, ${T.teal}, ${T.navy})`
                                    : T.line2,
                                }}
                              >
                                {heightPercent > 20 && day.loads > 0 && (
                                  <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold" style={{ color:"rgba(255,255,255,0.55)" }}>
                                    {day.loads}
                                  </span>
                                )}
                              </div>

                              {/* X-Axis Label */}
                              <span className="text-[9px] font-medium text-center mt-3 truncate absolute -bottom-5 w-full" style={{ color:T.muted }}>
                                {day.date}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </main>
        <BottomNav activeNav={activeNav} setActiveNav={setActiveNav} />
      </div>
    </div>
  );
}
